import { useState, useCallback } from 'react';
import { useRouter } from 'next/navigation';
import { useMutation } from '@apollo/client/react';
import { eventPath } from '../../utils/slugify';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import TextField from '@mui/material/TextField';
import { cancelEventMutation } from '../../apollo/events';

type Props = {
  open: boolean;
  onClose: () => void;
  eventId: string;
  eventName?: string;
};

const CancelEventDialog = ({ open, onClose, eventId, eventName }: Props) => {
  const router = useRouter();
  const [cancelEvent] = useMutation(cancelEventMutation);
  const [cancellationMessage, setCancellationMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleConfirm = useCallback(() => {
    setSubmitting(true);
    cancelEvent({
      variables: {
        id: eventId,
        message: cancellationMessage,
      },
    }).then(() => {
      onClose();
      router.push(eventPath(eventId, eventName));
    }).finally(() => {
      setSubmitting(false);
    });
  }, [eventId, eventName, cancellationMessage, cancelEvent, onClose, router]);

  const handleClose = useCallback(() => {
    if (!submitting) {
      onClose();
    }
  }, [submitting, onClose]);

  return (
    <Dialog open={open} onClose={handleClose}>
      <DialogTitle>Please Confirm</DialogTitle>
      <DialogContent>
        <DialogContentText>
          Are you sure you want to cancel this event?
        </DialogContentText>
        <TextField
          autoFocus
          id="cancellationMessage"
          label="Cancellation Message (optional)"
          fullWidth
          multiline
          rows={3}
          maxRows={20}
          variant="outlined"
          value={cancellationMessage}
          onChange={(e) => setCancellationMessage(e.target.value)}
          disabled={submitting}
          sx={{ mt: 2 }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={submitting}>
          Cancel
        </Button>
        <Button onClick={handleConfirm} color="primary" disabled={submitting} autoFocus>
          {submitting ? 'Cancelling...' : 'Confirm'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CancelEventDialog;
